import { ReactNode, useCallback } from 'react';
import { GetMeQuery } from '@/generated/gql/graphql';
import { useNavigationArtifacts } from '@/enterprise/data/useArtifacts';
import FullPageLoader from '@/shared/components/FullPageLoader';
import useUserDetails from '@/shared/data/useUserDetails';

/**
 * Blocks first paint until the NAVIGATION artifacts request settles, so eligibility-driven
 * nav items (e.g. the Fundbox "Term loans by Fundbox" entry) are present on the first
 * render instead of popping in after the side nav has painted.
 *
 * Only the navigation slice gates paint — MARKETING_MESSAGE artifacts load separately and
 * non-blocking (the marketing widget shows its own skeleton), keeping them off the
 * time-to-first-paint critical path.
 *
 * Must be rendered below `FeaturesProvider` (so the `enterprise-ui_useMarketingArtifacts`
 * flag is resolved) and above the navigation consumers.
 *
 * Behavior:
 * - Flag off → the query is disabled, `isLoading` is false, children render immediately
 *   (no Fundbox item — current behavior).
 * - Flag on + request in flight → render the full-page loader.
 * - Flag on + settled (success or error) → render children with a warm navigation cache,
 *   so `useNavigationItems` reads the data synchronously on first paint.
 *
 * Errors are non-fatal: the query uses `throwOnError: false`, so a failed request leaves
 * `isLoading` false and the app paints normally (nav without Fundbox).
 */
const ArtifactsGate = ({ children }: { children: ReactNode }) => {
  const { data: accountId } = useUserDetails(useCallback((user: GetMeQuery) => user.me?.account?.id, []));
  const { isLoading } = useNavigationArtifacts(accountId);

  if (isLoading) {
    return <FullPageLoader />;
  }

  return <>{children}</>;
};

export default ArtifactsGate;
